import { useEffect } from 'react'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { useCountUp } from '../lib/motion'
import { CoinNetworkRows, KV, MoneyRow, NavRow, Seg } from '../components/Controls'
import { useStore } from '../state/store'
import { CartAside } from './Sell'
import { fmt2, lineCents, pct, taxOnLines, taxOptional, TENDER_LABEL, type Coin, type SalePayment, type SplitPart, type Tender } from '../lib/types'

const TILES: { v: SalePayment; label: string; sub: string; icon: string }[] = [
  { v: 'card', label: 'Card', sub: 'Charged in Shopify POS', icon: 'M3 7h18v10H3zM3 10h18' },
  { v: 'cash', label: 'Cash', sub: 'Count it, then confirm', icon: 'M3 7h18v10H3zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z' },
  { v: 'zelle', label: 'Zelle', sub: 'Scans our Zelle QR', icon: 'M7 5h10L7 19h10' },
  { v: 'crypto', label: 'Crypto', sub: 'Scans the wallet QR', icon: 'M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3zM12 8v8M9 10.5l3-2 3 2' },
  { v: 'paypal', label: 'PayPal', sub: 'Scans our PayPal QR', icon: 'M7 20l2-14h6a4 4 0 0 1 0 8h-4l-1 6' },
  { v: 'split', label: 'Split', sub: 'Two or more of these', icon: 'M4 12h16M12 4v16' },
]
const PART_METHODS: { v: Tender; label: string }[] = [{ v: 'cash', label: 'Cash' }, { v: 'zelle', label: 'Zelle' }, { v: 'crypto', label: 'Crypto' }, { v: 'paypal', label: 'PayPal' }, { v: 'card', label: 'Card' }]
const Icon = ({ d }: { d: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>

export const partCents = (p: SplitPart) => Math.round(Number(p.amount || 0) * 100)
/** The card part of a split takes whatever the other parts leave; the app works it out so nobody does arithmetic at the register. */
export function splitAmounts(parts: SplitPart[], total: number) {
  const others = parts.filter((p) => p.method !== 'card').reduce((a, p) => a + partCents(p), 0)
  const card = parts.find((p) => p.method === 'card')
  const cardCents = card ? total - others : 0
  return { others, cardCents, remaining: total - others - (card ? Math.max(0, cardCents) : 0) }
}
export function splitOk(parts: SplitPart[], total: number) {
  if (parts.length < 2) return false
  if (parts.filter((p) => p.method === 'card').length > 1) return false
  const { cardCents, remaining } = splitAmounts(parts, total)
  if (parts.some((p) => p.method !== 'card' && partCents(p) <= 0)) return false
  if (parts.some((p) => p.method === 'card') && cardCents <= 0) return false
  if (parts.some((p) => p.method === 'crypto' && (!p.coin || (p.coin !== 'BTC' && !p.network)))) return false
  return remaining === 0
}

export function SellPay() {
  const nav = useNav()
  const { sale, setSale, customers, tax } = useStore()
  const empty = sale.lines.length === 0
  useEffect(() => { if (empty) nav('/sell', { replace: true }) }, [empty, nav])
  // motion: the drawn total rolls to its new number; `total` below is what the split and the sale use
  const totalShown = Math.round(useCountUp(sale.lines.reduce((a, l) => a + lineCents(l), 0) + ((taxOptional(sale) && sale.taxMode === 'none') ? 0 : taxOnLines(sale.lines, tax))))
  if (empty) return null
  const subtotal = sale.lines.reduce((a, l) => a + lineCents(l), 0)
  const noTax = taxOptional(sale) && sale.taxMode === 'none'
  const taxC = noTax ? 0 : taxOnLines(sale.lines, tax)
  const total = subtotal + taxC
  const exempt = sale.lines.filter((l) => lineCents(l) > 0 && lineCents(l) < tax.clothing_exempt_under_cents).length
  const cust = customers.find((c) => c.id === sale.customerId)
  const parts = sale.parts
  const setParts = (next: SplitPart[]) => setSale({ parts: next })
  const patchPart = (i: number, patch: Partial<SplitPart>) => setParts(parts.map((p, j) => (j === i ? { ...p, ...patch } : p)))
  const pick = (m: SalePayment) => {
    if (m === 'split' && parts.length < 2) setSale({ method: m, parts: [{ method: 'cash', amount: '', coin: 'USDC', network: '' }, { method: 'card', amount: '', coin: 'USDC', network: '' }] })
    else setSale({ method: m })
  }
  const { cardCents, remaining } = splitAmounts(parts, total)
  const ok = sale.lines.every((l) => lineCents(l) > 0) && !!sale.method && !!sale.customerId
    && (sale.method !== 'crypto' || (!!sale.coin && (sale.coin === 'BTC' || !!sale.network)))
    && (sale.method !== 'split' || splitOk(parts, total))
  return (
    <Page
      bar={<TopBar back="Prices" backTo="/sell/price" title="Payment" />}
      aside={<CartAside header="This sale" cta={() => nav('/sell/review')} ctaLabel="Review sale" ctaOff={!ok} ctaAmount={`${fmt2(totalShown)} with tax`} onLine={(i) => nav('/sell/price', { state: { step: i } })} />}
    >
      <div className="section-title">Payment method</div>
      <div className="paytiles">
        {TILES.map((m) => (
          <button key={m.v} type="button" className="paytile" aria-pressed={sale.method === m.v} onClick={() => pick(m.v)}>
            <span className="paytile__icon"><Icon d={m.icon} /></span>
            <span><span className="paytile__title">{m.label}</span><span className="paytile__sub">{m.sub}</span></span>
          </button>
        ))}
      </div>
      {sale.method === 'crypto' && (
        <>
          <div className="section-title">Crypto</div>
          <div className="card">
            <CoinNetworkRows coin={sale.coin} network={sale.network} onChange={(coin, network) => setSale({ coin: coin as Coin, network })} />
          </div>
          <div className="hint">The wallet QR for that coin and network comes up on the next screen, after the sale is recorded.</div>
        </>
      )}
      {sale.method === 'split' && (
        <>
          <div className="section-title">Split between</div>
          <div className="card">
            {parts.map((p, i) => (
              <div key={i} className="split__part">
                <div className="split__head">
                  <Seg<Tender> value={p.method} options={PART_METHODS.map((o) => ({ ...o, off: o.v === 'card' && parts.some((q, j) => j !== i && q.method === 'card') }))} onChange={(method) => patchPart(i, { method, amount: method === 'card' ? '' : p.amount })} />
                  <span style={{ flexGrow: 1 }} />
                  {parts.length > 2 && <button type="button" className="split__x" aria-label="Remove" onClick={() => setParts(parts.filter((_, j) => j !== i))}>×</button>}
                </div>
                {p.method === 'card'
                  ? <div className="row"><span className="row__label">Card takes the rest</span><span className={'row__value' + (cardCents > 0 ? ' set' : '')} style={{ color: cardCents <= 0 ? '#d70015' : undefined }}>{cardCents > 0 ? fmt2(cardCents) : 'Nothing left for the card'}</span></div>
                  : <MoneyRow id={`part${i}`} label={`${TENDER_LABEL[p.method]} amount`} value={p.amount} onChange={(amount) => patchPart(i, { amount })} placeholder="$0.00" decimal max={9} />}
                {p.method === 'crypto' && <div style={{ marginTop: 6 }}><CoinNetworkRows compact coin={p.coin} network={p.network} onChange={(coin, network) => patchPart(i, { coin: coin as Coin, network })} /></div>}
              </div>
            ))}
            {!parts.some((p) => p.method === 'card') && (
              <div className="row"><span className="row__label">Remaining</span><span className="row__value" style={{ color: remaining !== 0 ? '#d70015' : '#34c759', fontWeight: 600 }}>{fmt2(remaining)}</span>
                {remaining !== 0 && parts.length > 0 && <button type="button" className="seg__btn" onClick={() => { const last = parts.length - 1; patchPart(last, { amount: ((partCents(parts[last]) + remaining) / 100).toFixed(2) }) }}>Put it on {TENDER_LABEL[parts[parts.length - 1].method]}</button>}
              </div>
            )}
            <div style={{ padding: '10px 16px' }}>
              <button type="button" className="btn-secondary" style={{ height: 44, fontSize: 15 }} disabled={parts.length >= 4} onClick={() => setParts([...parts, { method: 'zelle', amount: '', coin: 'USDC', network: '' }])}>Add another payment</button>
            </div>
          </div>
          <div className="hint">Each part is collected on its own: cash counted, Zelle / PayPal / crypto scanned and confirmed, the card part charged in Shopify POS (the tile shows exactly what to type at Charge).</div>
        </>
      )}
      {sale.method === 'zelle' && <div className="hint">Starchive's Zelle QR comes up on the next screen. You confirm once it lands in the bank app.</div>}
      {sale.method === 'paypal' && <div className="hint">Starchive's PayPal QR comes up on the next screen with the amount filled in. You confirm once it shows in PayPal.</div>}
      <div className="section-title">Total owed</div>
      <div className="card review-rows">
        {taxOptional(sale) && <div className="row"><span className="row__label">Sales tax</span><Seg value={sale.taxMode} options={[{ v: 'add', label: `Add ${pct(tax.rate)}` }, { v: 'none', label: 'No tax' }]} onChange={(taxMode) => setSale({ taxMode })} /></div>}
        <KV k="Pieces" v={fmt2(subtotal)} />
        <KV k={noTax ? 'Sales tax' : `Sales tax (${pct(tax.rate)})${exempt ? ` · ${exempt} piece${exempt > 1 ? 's' : ''} under ${fmt2(tax.clothing_exempt_under_cents)} exempt` : ''}`} v={noTax ? 'None' : fmt2(taxC)} />
        <KV k="Total owed" v={fmt2(totalShown)} bold />
      </div>
      {sale.method === 'card' && <div className="hint">Shopify POS adds the tax itself at Charge; the number there should match this one.</div>}
      <div className="section-title">Customer</div>
      <div className="card">
        <NavRow label="Customer" value={cust ? `${cust.first_name} ${cust.last_name}`.trim() : 'Pick or add'} set={!!cust} onClick={() => nav('/customers?pick=sale')} />
        {cust && (
          <div style={{ padding: '4px 20px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flexGrow: 1, minWidth: 0, fontSize: 15, lineHeight: 1.5, color: '#3a3a3c' }}>
              <div>{cust.phone || 'No phone'}{cust.instagram ? ` · ${cust.instagram}` : ''}</div>
              <div>{cust.email || 'No email'}</div>
              {cust.notes && <div style={{ color: '#8e8e93' }}>{cust.notes}</div>}
              <div style={{ color: cust.in_shopify ? '#34c759' : '#ff9500', fontSize: 13 }}>{cust.in_shopify ? 'Matches their Shopify profile' : 'Not in Shopify yet'}</div>
            </div>
            <button type="button" className="btn-secondary" style={{ flexShrink: 0, width: 96, height: 44, fontSize: 15 }} onClick={() => nav(`/customers?pick=sale&edit=${cust.id}`)}>Edit</button>
            <button type="button" className="btn-secondary" style={{ flexShrink: 0, width: 96, height: 44, fontSize: 15, color: '#d70015' }} onClick={() => setSale({ customerId: '', customerName: '' })}>Remove</button>
          </div>
        )}
      </div>
      <div className="hint">Every sale has a customer on it. Pick them from the list (Shopify's customers included) or add them; check the phone with them (email is optional). Edits go to Shopify too. Remove takes them off this sale only; their record stays.</div>
    </Page>
  )
}
